<template>
  <div class="ef-page">

    <!-- ── Header + Search ───────────────────────────────────────────── -->
    <div class="page-header">
      <div class="page-header-left">
        <h1>Expectations Framework</h1>
        <div class="search-row">
        <div class="search-wrapper">
          <div class="search-input-row" :class="{ 'search-disabled': loading }">
            <span class="search-icon">🔍</span>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name, ISIN, WKN or RIC…"
              class="ric-input"
              autocomplete="off"
              :disabled="loading"
              @input="onSearchInput"
              @keydown.enter="onEnter"
              @keydown.escape="clearSearch"
            />
            <button v-if="searchQuery && !loading" class="search-clear-btn" @click="clearSearch">✕</button>
          </div>

          <div v-if="searchResults.length > 0" class="search-dropdown">
            <div
              v-for="item in searchResults"
              :key="item.ric"
              class="search-result-item"
              @click="selectSearchResult(item)"
            >
              <div class="result-name">{{ item.name }}</div>
              <div class="result-meta">
                <span class="result-ric">{{ item.ric }}</span>
                <span v-if="item.country_code" class="result-country">{{ item.country_code }}</span>
                <span v-if="item.industry" class="result-industry">{{ item.industry }}</span>
              </div>
            </div>
          </div>

          <div v-if="searchLoading" class="search-hint">
            <span class="spinner spinner-dark spinner-sm"></span> Searching…
          </div>
          <div v-else-if="searchQuery.length > 1 && !searchResults.length && !ricInput" class="search-hint">
            No results — press Enter to analyze "{{ searchQuery }}" directly as RIC
          </div>
        </div>

        <button class="btn-calc" :disabled="loading || !ricInput.trim()" @click="analyze">
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Analyzing…' : 'Calculate' }}
        </button>
        </div>
      </div>

      <button
        v-if="result && !loading"
        class="btn-watchlist"
        :class="{ 'btn-watchlist--added': isCurrentInWatchlist }"
        :disabled="watchlistAdding || isCurrentInWatchlist"
        @click="addCurrentToWatchlist"
      >
        <span class="wl-star">{{ isCurrentInWatchlist ? '★' : '☆' }}</span>
        {{ isCurrentInWatchlist ? 'In Watchlist' : watchlistAdding ? 'Adding…' : 'Add to Watchlist' }}
      </button>
    </div>

    <div v-if="error" class="error-box">{{ error }}</div>

    <template v-if="result && !loading">

      <!-- ── Meta Bar ─────────────────────────────────────────────────── -->
      <div class="meta-row">
        <div class="meta-chip ric-chip">{{ result.ric }}</div>
        <div class="meta-chip time-chip">{{ formatTs(result.timestamp) }}</div>
        <div v-if="result.mifp.period" class="meta-chip mifp-chip">
          MIFP&nbsp;{{ result.mifp.period }}
          &bull; FV&nbsp;{{ fmt(result.mifp.implied_fv, 2) }}
          &bull; Current&nbsp;{{ fmt(result.inputs.current_price, 2) }}
          &bull; Δ {{ sign(result.mifp.price_diff) }}{{ fmt(result.mifp.price_diff, 2) }}
        </div>
        <div v-if="result.errors.length" class="meta-chip warn-chip">
          {{ result.errors.length }} Warning(s)
        </div>
      </div>

      <!-- ── ANNAHMEN ──────────────────────────────────────────────────── -->
      <section class="section">
        <h2 class="section-title">
          Annahmen
          <span class="section-hint" v-if="chartsData">Click highlighted cards to view history</span>
          <button
            v-if="isDirty || recalculating"
            class="btn-recalc"
            :disabled="recalculating"
            @click="recalculate"
          >
            <span v-if="recalculating" class="spinner"></span>
            {{ recalculating ? 'Recalculating…' : '↺ Recalculate' }}
          </button>
        </h2>
        <div class="kpi-grid">

          <!-- Clickable + editable cards -->
          <div
            class="kpi-card"
            :class="{ 'has-chart': !!chartsData, 'kpi-edited': isFieldDirty('wacc') }"
            @click="openChart('wacc')"
          >
            <div class="kpi-label">WACC <span v-if="chartsData" class="chart-icon">↗</span></div>
            <div class="kpi-value-row">
              <input type="number" step="0.01"
                :value="Number(editedInputs.wacc).toFixed(2)"
                @change="e => setRaw('wacc', e.target.value)"
                @click.stop
                class="kpi-input"
              /><span class="kpi-unit">%</span>
            </div>
          </div>

          <div
            class="kpi-card"
            :class="{ 'has-chart': !!chartsData, 'kpi-edited': isFieldDirty('lt_revenue_growth') }"
            @click="openChart('sales_growth')"
          >
            <div class="kpi-label">LT Revenue Growth <span v-if="chartsData" class="chart-icon">↗</span></div>
            <div class="kpi-value-row">
              <input type="number" step="0.1"
                :value="(editedInputs.lt_revenue_growth * 100).toFixed(1)"
                @change="e => setPct('lt_revenue_growth', e.target.value)"
                @click.stop
                class="kpi-input"
              /><span class="kpi-unit">%</span>
            </div>
          </div>

          <div
            class="kpi-card"
            :class="{ 'has-chart': !!chartsData, 'kpi-edited': isFieldDirty('lt_ebit_margin') }"
            @click="openChart('ebit_margin')"
          >
            <div class="kpi-label">LT EBIT Margin <span v-if="chartsData" class="chart-icon">↗</span></div>
            <div class="kpi-value-row">
              <input type="number" step="0.1"
                :value="(editedInputs.lt_ebit_margin * 100).toFixed(1)"
                @change="e => setPct('lt_ebit_margin', e.target.value)"
                @click.stop
                class="kpi-input"
              /><span class="kpi-unit">%</span>
            </div>
          </div>

          <div
            class="kpi-card"
            :class="{ 'has-chart': !!chartsData, 'kpi-edited': isFieldDirty('inc_capex_rate') }"
            @click="openChart('capex_rate')"
          >
            <div class="kpi-label">Inc. CapEx Rate <span v-if="chartsData" class="chart-icon">↗</span></div>
            <div class="kpi-value-row">
              <input type="number" step="0.1"
                :value="(editedInputs.inc_capex_rate * 100).toFixed(1)"
                @change="e => setPct('inc_capex_rate', e.target.value)"
                @click.stop
                class="kpi-input"
              /><span class="kpi-unit">%</span>
            </div>
          </div>

          <div
            class="kpi-card"
            :class="{ 'has-chart': !!chartsData, 'kpi-edited': isFieldDirty('inc_nwc_rate') }"
            @click="openChart('nwc_rate')"
          >
            <div class="kpi-label">Inc. NWC Rate <span v-if="chartsData" class="chart-icon">↗</span></div>
            <div class="kpi-value-row">
              <input type="number" step="0.1"
                :value="(editedInputs.inc_nwc_rate * 100).toFixed(1)"
                @change="e => setPct('inc_nwc_rate', e.target.value)"
                @click.stop
                class="kpi-input"
              /><span class="kpi-unit">%</span>
            </div>
          </div>

          <!-- Editable, non-chart cards -->
          <div class="kpi-card" :class="{ 'kpi-edited': isFieldDirty('tax_rate') }">
            <div class="kpi-label">Tax Rate</div>
            <div class="kpi-value-row">
              <input type="number" step="0.1"
                :value="(editedInputs.tax_rate * 100).toFixed(1)"
                @change="e => setPct('tax_rate', e.target.value)"
                class="kpi-input"
              /><span class="kpi-unit">%</span>
            </div>
          </div>

          <div class="kpi-card" :class="{ 'kpi-edited': isFieldDirty('net_debt') }">
            <div class="kpi-label">Net Debt</div>
            <div class="kpi-value-row">
              <input type="number" step="0.01"
                :value="(editedInputs.net_debt / dcfScale.divisor).toFixed(2)"
                @change="e => setBnMn('net_debt', e.target.value)"
                class="kpi-input"
              /><span class="kpi-unit">{{ dcfScale.label }}</span>
            </div>
          </div>

          <div class="kpi-card" :class="{ 'kpi-edited': isFieldDirty('shares_outstanding') }">
            <div class="kpi-label">Shares Outstanding</div>
            <div class="kpi-value-row">
              <input type="number" step="1"
                :value="Math.round(editedInputs.shares_outstanding / 1e6)"
                @change="e => setM('shares_outstanding', e.target.value)"
                class="kpi-input"
              /><span class="kpi-unit">M</span>
            </div>
          </div>

          <div class="kpi-card" :class="{ 'kpi-edited': isFieldDirty('interest_expense') }">
            <div class="kpi-label">Interest Expense</div>
            <div class="kpi-value-row">
              <input type="number" step="0.01"
                :value="(editedInputs.interest_expense / dcfScale.divisor).toFixed(2)"
                @change="e => setBnMn('interest_expense', e.target.value)"
                class="kpi-input"
              /><span class="kpi-unit">{{ dcfScale.label }}</span>
            </div>
          </div>

          <div class="kpi-card" :class="{ 'kpi-edited': isFieldDirty('current_price') }">
            <div class="kpi-label">Current Price</div>
            <div class="kpi-value-row">
              <input type="number" step="0.01"
                :value="Number(editedInputs.current_price).toFixed(2)"
                @change="e => setRaw('current_price', e.target.value)"
                class="kpi-input"
              />
            </div>
          </div>

          <!-- Computed outputs — not editable -->
          <div class="kpi-card accent">
            <div class="kpi-label">Avg. Inc. SIC</div>
            <div class="kpi-value">{{ fmt(result.metrics.avg_incremental_sic, 2) }}x</div>
          </div>
          <div class="kpi-card accent">
            <div class="kpi-label">Implied ROIC</div>
            <div class="kpi-value">{{ fmt(result.metrics.implied_roic, 1) }}%</div>
          </div>
        </div>
      </section>

      <!-- ── DCF MODEL (transposed) ────────────────────────────────────── -->
      <section class="section">
        <h2 class="section-title">DCF Model
          <span class="section-hint">in {{ dcfScale.label }} unless noted · MIFP column highlighted</span>
          <div class="scale-toggle">
            <button
              class="scale-btn"
              :class="{ active: dcfScaleOverride === 'Mn' }"
              @click="dcfScaleOverride = 'Mn'"
            >Mio.</button>
            <button
              class="scale-btn"
              :class="{ active: dcfScaleOverride === 'Bn' }"
              @click="dcfScaleOverride = 'Bn'"
            >Bn.</button>
            <button
              class="scale-btn scale-btn-auto"
              :class="{ active: dcfScaleOverride === null }"
              @click="dcfScaleOverride = null"
            >Auto</button>
          </div>
        </h2>
        <div class="table-scroll">
          <table class="dcf-table">
            <thead>
              <tr>
                <th class="label-col sticky-col">Metric</th>
                <th
                  v-for="period in periods"
                  :key="period"
                  class="num-col"
                  :class="{ 'mifp-col-head': period === result.mifp.period }"
                >
                  {{ period }}
                  <span v-if="period === result.mifp.period" class="mifp-badge">MIFP</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in dcfRows"
                :key="row.field + row.format"
                :class="{
                  'row-sep': row.sep,
                  'row-bold': row.bold,
                  'row-sub': row.indent,
                }"
              >
                <td
                  class="label-col sticky-col"
                  :class="{ 'cell-italic': row.italic, 'label-indented': row.indent }"
                >
                  {{ row.label }}
                </td>
                <td
                  v-for="period in periods"
                  :key="period"
                  class="num-col"
                  :class="{
                    'mifp-col': period === result.mifp.period,
                    'cell-italic': row.italic,
                  }"
                >
                  {{ formatCell(period, row) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ── SENSITIVITIES ─────────────────────────────────────────────── -->
      <section class="section">
        <h2 class="section-title">
          Sensitivities
          <span class="section-hint">Price impact of +10 % driver shock (in price currency)</span>
        </h2>
        <div class="sens-grid">
          <div
            v-for="(val, key) in result.sensitivities"
            :key="key"
            class="sens-card"
            :class="val > 0 ? 'pos' : val < 0 ? 'neg' : 'neu'"
          >
            <div class="sens-label">{{ sensLabel(key) }}</div>
            <div class="sens-value">{{ val > 0 ? '+' : '' }}{{ fmt(val, 2) }}</div>
          </div>
        </div>
      </section>

      <!-- ── SCORING ──────────────────────────────────────────────────── -->
      <section class="section">
        <h2 class="section-title">
          Scoring
          <span v-if="result.scoring" class="signal-badge" :class="signalClass(result.scoring.signal)">
            {{ result.scoring.signal }}
          </span>
          <span v-else class="section-hint">Not yet scored</span>
        </h2>
        <template v-if="result.scoring">
          <div class="scoring-layout">
            <div class="score-main-card">
              <div class="score-composite">{{ fmt(result.scoring.composite_score, 2) }}</div>
              <div class="score-composite-label">/ 5 Composite Score</div>
              <div class="score-level-row">
                <span v-for="i in 5" :key="i" class="score-dot" :class="i <= result.scoring.expectation_level ? 'dot-on' : 'dot-off'"></span>
                <span class="score-level-label">{{ result.scoring.expectation_label }}</span>
              </div>
            </div>
            <div class="score-detail">
              <div class="score-sub-grid">
                <div class="score-sub-row">
                  <span class="score-sub-label">Valuation vs. Hist.</span>
                  <span class="score-dots-sm">
                    <span v-for="i in 5" :key="i" class="score-dot-sm" :class="i <= result.scoring.valuation_vs_hist ? 'dot-on' : 'dot-off'"></span>
                  </span>
                  <span class="score-sub-num">{{ result.scoring.valuation_vs_hist }}/5</span>
                </div>
                <div class="score-sub-row">
                  <span class="score-sub-label">Sensitivity</span>
                  <span class="score-dots-sm">
                    <span v-for="i in 5" :key="i" class="score-dot-sm" :class="i <= result.scoring.sensitivity_score ? 'dot-on' : 'dot-off'"></span>
                  </span>
                  <span class="score-sub-num">{{ result.scoring.sensitivity_score }}/5</span>
                </div>
                <div class="score-sub-row">
                  <span class="score-sub-label">Data Quality</span>
                  <span class="score-dots-sm">
                    <span v-for="i in 5" :key="i" class="score-dot-sm" :class="i <= result.scoring.dq_score ? 'dot-on' : 'dot-off'"></span>
                  </span>
                  <span class="score-sub-num">{{ result.scoring.dq_score }}/5</span>
                </div>
              </div>
              <p class="score-rationale">{{ result.scoring.rationale }}</p>
              <div v-if="result.scoring.flags && result.scoring.flags.length" class="score-flags">
                <span v-for="flag in result.scoring.flags" :key="flag" class="score-flag">{{ flag }}</span>
              </div>
              <div class="score-meta">
                Scored {{ formatTs(result.scoring.scored_at) }}&nbsp;·&nbsp;{{ result.scoring.scoring_method }}
              </div>
            </div>
          </div>
        </template>
      </section>

      <!-- ── DATA QUALITY FLAGS ────────────────────────────────────────── -->
      <section class="section">
        <h2 class="section-title">Data Quality</h2>
        <div class="flags-grid">
          <div
            v-for="(flag, key) in result.data_quality_flags"
            :key="key"
            class="flag-row"
          >
            <span class="flag-key">{{ flagKey(key) }}</span>
            <span class="flag-badge" :class="flagClass(flag.value)">{{ flag.label }}</span>
          </div>
        </div>
      </section>

    </template>

    <!-- ── Chart Modal ───────────────────────────────────────────────── -->
    <div v-if="activeChart" class="chart-overlay" @click.self="closeChart">
      <div class="chart-modal">
        <div class="chart-modal-header">
          <div>
            <h3 class="chart-modal-title">{{ chartTitles[activeChart] }}</h3>
            <span class="chart-modal-sub">{{ result && result.ric }} · 10-year History &amp; 3-year Forecast</span>
          </div>
          <button class="btn-close" @click="closeChart">✕</button>
        </div>
        <div class="chart-canvas-wrap">
          <canvas ref="chartCanvas"></canvas>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import { analyzeRic, fetchCharts, recalculateRic, searchInstruments, addToWatchlist, fetchMyWatchlist } from '@/services/api'

const DCF_ROWS = [
  { label: 'Revenue',              field: 'revenue',            format: 'bn',     bold: false, italic: false, sep: false, indent: false },
  { label: 'EBIT Margin',          field: 'ebit_margin',        format: 'pct',    bold: false, italic: true,  sep: false, indent: true  },
  { label: 'EBIT',                 field: 'ebit_nominal',       format: 'bn',     bold: false, italic: false, sep: false, indent: false },
  { label: 'Taxes',                field: 'taxes',              format: 'bn',     bold: false, italic: false, sep: false, indent: true  },
  { label: 'NOPAT',                field: 'nopat',              format: 'bn',     bold: true,  italic: false, sep: true,  indent: false },
  { label: 'Interest (after Tax)', field: 'interest_after_tax', format: 'bn',     bold: false, italic: false, sep: false, indent: true  },
  { label: 'Δ Revenue',            field: 'delta_revenue',      format: 'bn',     bold: false, italic: false, sep: false, indent: true  },
  { label: 'Inc. CapEx',           field: 'inc_capex',          format: 'bn',     bold: false, italic: false, sep: false, indent: true  },
  { label: 'Inc. NWC',             field: 'inc_nwc',            format: 'bn',     bold: false, italic: false, sep: false, indent: true  },
  { label: 'FCFF',                 field: 'fcff',               format: 'bn',     bold: true,  italic: false, sep: true,  indent: false },
  { label: 'Discount Factor',      field: 'discount_factor',    format: 'factor', bold: false, italic: true,  sep: false, indent: true  },
  { label: 'PV FCFF',              field: 'pv_fcff',            format: 'bn',     bold: false, italic: false, sep: false, indent: false },
  { label: 'Cumul. PV FCFF',       field: 'pv_fcff',            format: 'cum_pv', bold: false, italic: true,  sep: false, indent: true  },
  { label: 'Terminal Value',       field: 'terminal_value',     format: 'bn',     bold: false, italic: false, sep: false, indent: true  },
  { label: 'Enterprise Value',     field: 'enterprise_value',   format: 'bn',     bold: true,  italic: false, sep: true,  indent: false },
  { label: 'Net Debt',             field: 'net_debt',           format: 'bn',     bold: false, italic: false, sep: false, indent: true  },
  { label: 'Shareholder Value',    field: 'shareholder_value',  format: 'bn',     bold: false, italic: false, sep: false, indent: false },
  { label: 'FV / Share',           field: 'fv_per_share',       format: 'num2',   bold: true,  italic: false, sep: true,  indent: false },
]

const CHART_TITLES = {
  wacc:        'WACC',
  sales_growth:'Sales Growth',
  ebit_margin: 'EBIT Margin',
  capex_rate:  'Inc. CapEx Rate',
  nwc_rate:    'Inc. NWC Rate',
}

export default {
  name: 'ExpectationsFramework',

  data() {
    return {
      ricInput:      '',
      searchQuery:   '',
      searchResults: [],
      searchLoading: false,
      searchTimer:   null,
      loading:       false,
      error:         null,
      result:        null,
      editedInputs:  null,
      estimates:     null,
      recalculating:    false,
      chartsData:       null,
      activeChart:      null,
      chartInstance:    null,
      dcfRows:          DCF_ROWS,
      chartTitles:      CHART_TITLES,
      dcfScaleOverride: null,
      myWatchlist:      [],
      watchlistAdding:  false,
    }
  },

  computed: {
    periods() {
      return (this.result?.dcf_model || []).map(r => r.period)
    },
    dcfByPeriod() {
      const map = {}
      for (const row of this.result?.dcf_model || []) map[row.period] = row
      return map
    },
    dcfScale() {
      if (this.dcfScaleOverride === 'Bn') return { divisor: 1e9, label: 'Bn' }
      if (this.dcfScaleOverride === 'Mn') return { divisor: 1e6, label: 'Mio.' }
      const peak = Math.max(
        0,
        ...(this.result?.dcf_model || []).map(r => Math.abs(r.revenue || 0))
      )
      return peak >= 1e9
        ? { divisor: 1e9, label: 'Bn' }
        : { divisor: 1e6, label: 'Mio.' }
    },
    isCurrentInWatchlist() {
      const ric = this.result?.ric
      if (!ric) return false
      const upper = ric.toUpperCase()
      return this.myWatchlist.some(e => e.ric.toUpperCase() === upper)
    },
    isDirty() {
      if (!this.editedInputs || !this.result?.inputs) return false
      const fields = ['wacc', 'lt_revenue_growth', 'lt_ebit_margin', 'inc_capex_rate',
                      'inc_nwc_rate', 'tax_rate', 'interest_expense', 'net_debt',
                      'shares_outstanding', 'current_price']
      return fields.some(f => {
        const a = this.editedInputs[f] ?? 0
        const b = this.result.inputs[f] ?? 0
        return Math.abs(a - b) > Math.max(Math.abs(b) * 1e-6, 1e-10)
      })
    },
  },

  watch: {
    '$route.query.ric': {
      immediate: true,
      handler(ric) {
        if (ric && ric !== this.ricInput) {
          this.ricInput    = ric
          this.searchQuery = ric
          this.analyze()
        }
      },
    },
  },

  mounted() {
    this._onEsc = (e) => { if (e.key === 'Escape') this.closeChart() }
    window.addEventListener('keydown', this._onEsc)
    this._onOutsideClick = (e) => {
      if (!this.$el.contains(e.target)) this.searchResults = []
    }
    document.addEventListener('click', this._onOutsideClick)
    this.loadMyWatchlist()
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this._onEsc)
    document.removeEventListener('click', this._onOutsideClick)
    clearTimeout(this.searchTimer)
    this.destroyChart()
  },

  methods: {
    // ── Watchlist ────────────────────────────────────────────────────────
    async loadMyWatchlist() {
      try {
        const data = await fetchMyWatchlist()
        this.myWatchlist = data.watchlist || []
      } catch { /* silent */ }
    },

    async addCurrentToWatchlist() {
      if (!this.result?.ric || this.watchlistAdding) return
      this.watchlistAdding = true
      try {
        await addToWatchlist(this.result.ric)
        await this.loadMyWatchlist()
      } catch { /* silent */ } finally {
        this.watchlistAdding = false
      }
    },

    // ── Search ───────────────────────────────────────────────────────────
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.ricInput      = ''
      this.searchResults = []
      if (this.searchQuery.length < 2) return
      this.searchTimer = setTimeout(() => this.runSearch(), 350)
    },

    async runSearch() {
      this.searchLoading = true
      try {
        const data = await searchInstruments(this.searchQuery)
        this.searchResults = data.results || []
      } catch {
        this.searchResults = []
      } finally {
        this.searchLoading = false
      }
    },

    selectSearchResult(item) {
      this.ricInput      = item.ric
      this.searchQuery   = item.name
      this.searchResults = []
      this.analyze()
    },

    onEnter() {
      if (this.searchResults.length > 0) {
        this.selectSearchResult(this.searchResults[0])
        return
      }
      // treat raw input as direct RIC entry
      this.ricInput      = this.searchQuery.trim()
      this.searchResults = []
      this.analyze()
    },

    clearSearch() {
      this.searchQuery   = ''
      this.ricInput      = ''
      this.searchResults = []
    },

    async analyze() {
      const ric = this.ricInput.trim()
      if (!ric) return
      this.loading = true
      this.error = null
      this.result = null
      this.editedInputs = null
      this.estimates = null
      this.chartsData = null
      try {
        const [{ data }] = await Promise.all([
          analyzeRic(ric),
          this.loadMyWatchlist(),
        ])
        if (data.error) {
          this.error = data.error
        } else {
          this.result = data
          this.editedInputs = { ...data.inputs }
          this.estimates = data.estimates || null
          // fetch charts in background — don't block or show errors
          fetchCharts(ric).then(r => { this.chartsData = r.data.charts }).catch(() => {})
        }
      } catch (e) {
        this.error = e.response?.data?.error || e.message || 'Request failed'
      } finally {
        this.loading = false
      }
    },

    openChart(key) {
      if (!this.chartsData?.[key]) return
      if (this.activeChart === key) { this.closeChart(); return }
      this.destroyChart()
      this.activeChart = key
      // wait for v-if to mount the canvas, then wait for browser paint
      this.$nextTick(() => requestAnimationFrame(() => this.renderChart(key)))
    },

    closeChart() {
      this.destroyChart()
      this.activeChart = null
    },

    destroyChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy()
        this.chartInstance = null
      }
    },

    renderChart(key) {
      const raw    = this.chartsData?.[key]
      const canvas = this.$refs.chartCanvas
      if (!raw || !canvas) return

      const multiply = key === 'wacc' ? 1 : 100
      const dec      = key === 'wacc' ? 2 : 1
      const labels   = raw.labels

      const historicalDs = raw.datasets.find(d => d.name === 'Historical')
      const forecastDs   = raw.datasets.find(d => d.name === 'Forecast')

      const histVals = (historicalDs?.data || []).map(v => v != null ? +(v * multiply) : null)
      const foreVals = (forecastDs?.data   || []).map(v => v != null ? +(v * multiply) : null)

      // compute stats from non-null historical values only
      const histNonNull = histVals.filter(v => v != null)
      const avg = histNonNull.length ? histNonNull.reduce((a, b) => a + b, 0) / histNonNull.length : null
      const max = histNonNull.length ? Math.max(...histNonNull) : null
      const min = histNonNull.length ? Math.min(...histNonNull) : null

      const refLine = (label, value, color) => ({
        label,
        data: labels.map(() => value),
        borderColor: color,
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        borderDash: [5, 4],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        tension: 0,
        order: 3,
      })

      const datasets = [
        {
          label: 'Historical',
          data: histVals,
          borderColor: '#003366',
          backgroundColor: 'rgba(0, 51, 102, 0.07)',
          borderWidth: 2.5,
          pointRadius: 4,
          pointBackgroundColor: '#003366',
          pointBorderColor: '#fff',
          pointBorderWidth: 1.5,
          pointHoverRadius: 6,
          fill: true,
          tension: 0.3,
          spanGaps: false,
          order: 1,
        },
        {
          label: 'Forecast',
          data: foreVals,
          borderColor: '#f59e0b',
          backgroundColor: 'rgba(245, 158, 11, 0.07)',
          borderWidth: 2.5,
          borderDash: [6, 4],
          pointRadius: 5,
          pointBackgroundColor: '#f59e0b',
          pointBorderColor: '#fff',
          pointBorderWidth: 1.5,
          pointHoverRadius: 7,
          fill: true,
          tension: 0.3,
          spanGaps: false,
          order: 1,
        },
      ]

      if (avg != null) datasets.push(refLine(`Ø  ${avg.toFixed(dec)} %`, avg, '#9ca3af'))
      if (max != null) datasets.push(refLine(`Max  ${max.toFixed(dec)} %`, max, '#ef4444'))
      if (min != null) datasets.push(refLine(`Min  ${min.toFixed(dec)} %`, min, '#22c55e'))

      this.chartInstance = new Chart(canvas, {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              position: 'top',
              labels: { font: { size: 11 }, usePointStyle: true, pointStyleWidth: 12, padding: 16 },
            },
            tooltip: {
              filter: item => item.parsed.y != null,
              callbacks: {
                label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y.toFixed(dec)} %`,
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 11 } },
            },
            y: {
              grid: { color: 'rgba(0,0,0,0.06)' },
              ticks: { font: { size: 11 }, callback: v => v.toFixed(dec) + ' %' },
            },
          },
        },
      })
    },

    // ── Editable inputs ─────────────────────────────────────────────────
    isFieldDirty(field) {
      if (!this.editedInputs || !this.result?.inputs) return false
      const a = this.editedInputs[field] ?? 0
      const b = this.result.inputs[field] ?? 0
      return Math.abs(a - b) > Math.max(Math.abs(b) * 1e-6, 1e-10)
    },

    setRaw(field, v)  { const n = parseFloat(v); if (!isNaN(n)) this.editedInputs[field] = n },
    setPct(field, v)  { const n = parseFloat(v); if (!isNaN(n)) this.editedInputs[field] = n / 100 },
    setBnMn(field, v) { const n = parseFloat(v); if (!isNaN(n)) this.editedInputs[field] = n * this.dcfScale.divisor },
    setM(field, v)    { const n = parseFloat(v); if (!isNaN(n)) this.editedInputs[field] = n * 1e6 },

    async recalculate() {
      const ric = this.result?.ric
      if (!ric || !this.editedInputs) return
      this.recalculating = true
      this.error = null
      try {
        const revenue_by_period = Object.fromEntries(
          (this.estimates?.revenue || []).map(r => [r.period, r['tr.revenuemean']])
        )
        const margin_by_period = Object.fromEntries(
          (this.estimates?.margins || []).map(r => [r.period, r.ebit_margin_mean])
        )
        const { data } = await recalculateRic(ric, {
          ...this.editedInputs,
          revenue_by_period,
          margin_by_period,
        })
        this.result = {
          ...this.result,
          inputs:        data.inputs,
          dcf_model:     data.dcf_model,
          mifp:          data.mifp,
          metrics:       data.metrics,
          sensitivities: data.sensitivities,
        }
        this.editedInputs = { ...data.inputs }
      } catch (e) {
        this.error = e.response?.data?.error || e.message || 'Recalculation failed'
      } finally {
        this.recalculating = false
      }
    },

    // ── Formatters ──────────────────────────────────────────────────────
    formatCell(period, row) {
      const div = this.dcfScale.divisor
      if (row.format === 'cum_pv') {
        let sum = 0
        for (const p of this.periods) {
          sum += (this.dcfByPeriod[p]?.pv_fcff || 0)
          if (p === period) break
        }
        return (sum / div).toFixed(2)
      }
      const val = this.dcfByPeriod[period]?.[row.field]
      if (val == null) return '—'
      switch (row.format) {
        case 'bn':     return (val / div).toFixed(2)
        case 'pct':    return (val * 100).toFixed(1) + '%'
        case 'factor': return val.toFixed(4)
        case 'num2':   return Number(val).toFixed(2)
        default:       return val
      }
    },

    fmt(val, dec = 2) {
      if (val == null) return '—'
      return Number(val).toFixed(dec)
    },
    fmtPct(val) {
      if (val == null) return '—'
      return (val * 100).toFixed(1) + '%'
    },
    fmtBn(val) {
      if (val == null) return '—'
      return (val / this.dcfScale.divisor).toFixed(2) + ' ' + this.dcfScale.label
    },
    fmtM(val) {
      if (val == null) return '—'
      return Math.round(val / 1e6).toLocaleString('de-DE') + ' M'
    },
    sign(val) { return val != null && val >= 0 ? '+' : '' },
    formatTs(ts) {
      if (!ts) return ''
      return new Date(ts).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },
    sensLabel(key) {
      const map = {
        WACC: 'WACC', EBIT_MARGIN: 'EBIT Margin',
        SALES_GROWTH: 'Sales Growth', INC_CAPEX: 'Inc. CapEx', INC_NWC: 'Inc. NWC',
      }
      return map[key] || key
    },
    flagKey(key) {
      const map = {
        wacc: 'WACC', shares: 'Shares', net_debt: 'Net Debt', revenue: 'Revenue',
        ebit: 'EBIT', tax_rate: 'Tax Rate', tax_check: 'Tax Check',
        capex_rate: 'CapEx Rate', nwc_rate: 'NWC Rate', interest: 'Interest', price: 'Price',
      }
      return map[key] || key
    },
    flagClass(value) {
      if (value === 1) return 'flag-green'
      if (value === 2) return 'flag-yellow'
      if (value === 3) return 'flag-orange'
      return 'flag-red'
    },
    signalClass(signal) {
      if (!signal) return 'signal-neutral'
      const s = signal.toLowerCase()
      if (s.includes('sehr interessant') || s.includes('stark')) return 'signal-strong'
      if (s.includes('interessant')) return 'signal-positive'
      if (s.includes('uninteressant') || s.includes('negativ') || s.includes('avoid')) return 'signal-negative'
      return 'signal-neutral'
    },
  },
}
</script>

<style scoped>
.ef-page { max-width: 1400px; }

/* ── Header ───────────────────────────────────────────────────────────── */
.page-header {
  margin-bottom: 32px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}
.page-header-left { flex: 1; min-width: 0; }
.page-header h1 { font-size: 26px; font-weight: 700; color: #003366; margin-bottom: 18px; }
.search-row { display: flex; gap: 12px; align-items: flex-start; }

.btn-watchlist {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px;
  background: #fff;
  color: #003366;
  border: 1.5px solid #003366;
  border-radius: 7px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 3px;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.btn-watchlist:hover:not(:disabled) { background: #f0f7ff; }
.btn-watchlist--added {
  background: #003366;
  color: #fff;
  border-color: #003366;
  cursor: default;
}
.btn-watchlist--added:hover { background: #003366; }
.btn-watchlist:disabled:not(.btn-watchlist--added) { opacity: 0.6; cursor: not-allowed; }
.wl-star { font-size: 16px; line-height: 1; }


.search-wrapper { position: relative; }

.search-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  width: 340px;
  padding: 0 12px;
  border: 1.5px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.search-input-row:focus-within {
  border-color: #003366;
  box-shadow: 0 0 0 3px rgba(0, 51, 102, 0.10);
}
.search-input-row.search-disabled { background: #f9fafb; opacity: 0.7; }

.search-icon { font-size: 15px; opacity: 0.45; flex-shrink: 0; }

.ric-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  background: transparent;
  color: #111827;
  font-family: inherit;
  min-width: 0;
}
.ric-input::placeholder { color: #9ca3af; }

.search-clear-btn {
  background: none; border: none; cursor: pointer;
  color: #9ca3af; font-size: 13px; padding: 2px 4px; flex-shrink: 0;
}
.search-clear-btn:hover { color: #374151; }

.search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10);
  z-index: 100;
  overflow: hidden;
}
.search-result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.1s;
}
.search-result-item:hover { background: #f0f7ff; }
.result-name { font-size: 14px; font-weight: 500; color: #1a1a2e; }
.result-meta { display: flex; gap: 8px; align-items: center; flex-shrink: 0; }
.result-ric     { font-size: 12px; font-weight: 700; color: #003366; font-family: monospace; }
.result-country { font-size: 11px; color: #6b7280; }
.result-industry { font-size: 11px; color: #9ca3af; }

.search-hint {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 2px 0;
  font-size: 12px; color: #9ca3af;
}
.spinner-sm  { width: 13px; height: 13px; border-width: 1.5px; }
.spinner-dark { border-color: #e5e7eb; border-top-color: #003366; }
.btn-calc {
  height: 42px; padding: 0 28px; background: #003366; color: #fff;
  border: none; border-radius: 6px; font-size: 15px; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 8px;
  transition: background 0.15s;
}
.btn-calc:hover:not(:disabled) { background: #004080; }
.btn-calc:disabled { opacity: 0.55; cursor: not-allowed; }
.spinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff;
  border-radius: 50%; animation: spin 0.7s linear infinite; flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }
.error-box {
  background: #fef2f2; border: 1px solid #fca5a5; border-radius: 8px;
  padding: 14px 18px; color: #b91c1c; font-size: 14px; margin-bottom: 24px;
}

/* ── Meta Bar ─────────────────────────────────────────────────────────── */
.meta-row { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 32px; align-items: center; }
.meta-chip { padding: 5px 14px; border-radius: 20px; font-size: 13px; font-weight: 600; }
.ric-chip  { background: #003366; color: #fff; letter-spacing: 1px; font-size: 14px; }
.time-chip { background: #f3f4f6; color: #6b7280; }
.mifp-chip { background: #dbeafe; color: #1e40af; }
.warn-chip { background: #fef3c7; color: #92400e; }

/* ── Section ──────────────────────────────────────────────────────────── */
.section { margin-bottom: 44px; }
.section-title {
  font-size: 16px; font-weight: 700; color: #003366; margin-bottom: 16px;
  padding-bottom: 10px; border-bottom: 2px solid #e5e7eb;
  display: flex; align-items: baseline; gap: 10px;
}
.section-hint { font-size: 12px; font-weight: 400; color: #9ca3af; }

/* ── KPI Cards ────────────────────────────────────────────────────────── */
.kpi-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px;
}
.kpi-card {
  background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px 16px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.kpi-card.accent { background: #eff6ff; border-color: #bfdbfe; }

.kpi-card.has-chart {
  cursor: pointer;
  border-color: #93c5fd;
  background: #f0f6ff;
}
.kpi-card.has-chart:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59,130,246,0.15);
}

.kpi-label {
  font-size: 10.5px; font-weight: 600; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;
  display: flex; align-items: center; gap: 5px;
}
.chart-icon {
  font-size: 11px; color: #3b82f6; font-style: normal;
}
.kpi-value { font-size: 20px; font-weight: 700; color: #111827; line-height: 1.2; }

/* ── DCF Table ────────────────────────────────────────────────────────── */
.table-scroll { overflow-x: auto; border: 1px solid #e5e7eb; border-radius: 8px; }
.dcf-table { border-collapse: separate; border-spacing: 0; font-size: 12.5px; width: 100%; }
.dcf-table thead tr { background: #003366; }
.dcf-table th {
  padding: 10px 14px; font-weight: 600; font-size: 12px; color: #fff;
  white-space: nowrap; border-bottom: 1px solid rgba(255,255,255,0.15); text-align: right;
}
.dcf-table th.label-col { text-align: left; }
.dcf-table td {
  padding: 7px 14px; text-align: right; color: #374151;
  white-space: nowrap; font-variant-numeric: tabular-nums; border-bottom: 1px solid #f3f4f6;
}
.dcf-table td.label-col { text-align: left; }
.dcf-table tbody tr:last-child td { border-bottom: none; }
.dcf-table tbody tr:hover td { background: #fafafa; }
.sticky-col { position: sticky; left: 0; z-index: 2; min-width: 180px; background: #fff; }
thead .sticky-col { background: #003366; z-index: 3; }
.row-sep td { border-top: 1.5px solid #d1d5db !important; }
.row-bold td { font-weight: 700; color: #111827; }
.row-sub td { color: #6b7280; font-size: 12px; }
.label-indented { padding-left: 28px !important; }
.cell-italic { font-style: italic; color: #6b7280; }
thead th.mifp-col-head {
  background: #c8d3e0 !important; color: #001f3f;
  border-left: 2px solid #6b7280; border-right: 2px solid #6b7280; border-top: 2px solid #6b7280;
}
tbody td.mifp-col {
  background: #f0f2f5 !important; border-left: 2px solid #9ca3af; border-right: 2px solid #9ca3af;
}
tbody tr:last-child td.mifp-col { border-bottom: 2px solid #9ca3af; }
.dcf-table tbody tr:hover td.mifp-col { background: #e8ebf0 !important; }
.mifp-badge {
  display: block; font-size: 9px; font-weight: 700; letter-spacing: 0.5px;
  color: #001f3f; margin-top: 2px; opacity: 0.75;
}

/* ── Sensitivities ────────────────────────────────────────────────────── */
.sens-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; }
.sens-card { border-radius: 8px; padding: 16px; border: 1px solid; }
.sens-card.pos { background: #f0fdf4; border-color: #86efac; }
.sens-card.neg { background: #fef2f2; border-color: #fca5a5; }
.sens-card.neu { background: #f9fafb; border-color: #e5e7eb; }
.sens-label { font-size: 10.5px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
.sens-value { font-size: 24px; font-weight: 700; }
.sens-card.pos .sens-value { color: #16a34a; }
.sens-card.neg .sens-value { color: #dc2626; }
.sens-card.neu .sens-value { color: #6b7280; }

/* ── Flags ────────────────────────────────────────────────────────────── */
.flags-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); }
.flag-row { display: flex; align-items: center; gap: 16px; padding: 9px 4px; border-bottom: 1px solid #f3f4f6; }
.flag-key { min-width: 110px; font-size: 13px; font-weight: 600; color: #374151; }
.flag-badge { font-size: 12px; font-weight: 600; padding: 3px 12px; border-radius: 12px; }
.flag-green  { background: #dcfce7; color: #15803d; }
.flag-yellow { background: #fef9c3; color: #a16207; }
.flag-orange { background: #ffedd5; color: #c2410c; }
.flag-red    { background: #fee2e2; color: #b91c1c; }

/* ── Chart Overlay & Modal ────────────────────────────────────────────── */
.chart-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
  animation: fadeIn 0.15s ease;
}

@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

.chart-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  width: min(780px, 95vw);
  animation: slideUp 0.18s ease;
  overflow: hidden;
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}

.chart-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f3f4f6;
}

.chart-modal-title {
  font-size: 17px;
  font-weight: 700;
  color: #003366;
  margin-bottom: 3px;
}

.chart-modal-sub {
  font-size: 12px;
  color: #9ca3af;
}

.btn-close {
  width: 32px; height: 32px;
  border: 1.5px solid #e5e7eb; border-radius: 6px;
  background: #fff; cursor: pointer; font-size: 14px; color: #6b7280;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.1s, color 0.1s; flex-shrink: 0;
}
.btn-close:hover { background: #f3f4f6; color: #111827; }

.chart-canvas-wrap {
  padding: 20px 24px 24px;
  height: 340px;
  position: relative;
}

/* ── Editable KPI inputs ──────────────────────────────────────────────── */
.kpi-value-row {
  display: flex;
  align-items: baseline;
  gap: 3px;
}

.kpi-input {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  font-family: inherit;
  background: transparent;
  border: none;
  border-bottom: 1.5px solid transparent;
  outline: none;
  padding: 0;
  min-width: 0;
  flex: 1;
  line-height: 1.2;
  transition: border-color 0.15s, color 0.15s;
  appearance: textfield;
  -moz-appearance: textfield;
}
.kpi-input::-webkit-outer-spin-button,
.kpi-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.kpi-input:focus { border-bottom-color: #003366; }

.kpi-unit {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Dirty state */
.kpi-edited { border-color: #f59e0b !important; background: #fffbeb !important; }
.kpi-edited .kpi-input { color: #b45309; }
.kpi-edited .kpi-unit  { color: #b45309; }
.kpi-card.has-chart .kpi-input { cursor: text; }

/* ── Scale toggle ─────────────────────────────────────────────────────── */
.scale-toggle {
  display: flex; gap: 0; margin-left: auto; border: 1.5px solid #d1d5db;
  border-radius: 6px; overflow: hidden; flex-shrink: 0;
}
.scale-btn {
  padding: 3px 11px; font-size: 12px; font-weight: 600; color: #6b7280;
  background: #fff; border: none; border-right: 1px solid #d1d5db;
  cursor: pointer; transition: background 0.12s, color 0.12s; white-space: nowrap;
}
.scale-btn:last-child { border-right: none; }
.scale-btn:hover:not(.active) { background: #f3f4f6; color: #374151; }
.scale-btn.active { background: #003366; color: #fff; }
.scale-btn-auto { color: #9ca3af; font-weight: 400; }
.scale-btn-auto.active { background: #6b7280; color: #fff; }

/* ── Scoring ──────────────────────────────────────────────────────────── */
.signal-badge {
  font-size: 12px; font-weight: 700; padding: 3px 12px; border-radius: 12px;
  letter-spacing: 0.3px;
}
.signal-strong   { background: #dcfce7; color: #15803d; }
.signal-positive { background: #dbeafe; color: #1d4ed8; }
.signal-neutral  { background: #f3f4f6; color: #6b7280; }
.signal-negative { background: #fee2e2; color: #b91c1c; }

.scoring-layout {
  display: flex; gap: 28px; align-items: flex-start; flex-wrap: wrap;
}

.score-main-card {
  background: #f0f6ff; border: 1px solid #bfdbfe; border-radius: 10px;
  padding: 20px 24px; min-width: 180px; text-align: center; flex-shrink: 0;
}
.score-composite {
  font-size: 48px; font-weight: 800; color: #003366; line-height: 1;
}
.score-composite-label {
  font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase;
  letter-spacing: 0.5px; margin: 4px 0 14px;
}
.score-level-row {
  display: flex; align-items: center; justify-content: center; gap: 5px; flex-wrap: wrap;
}
.score-dot {
  display: inline-block; width: 12px; height: 12px; border-radius: 50%;
}
.score-dot.dot-on  { background: #003366; }
.score-dot.dot-off { background: #d1d5db; }
.score-level-label {
  font-size: 12px; font-weight: 700; color: #1e40af; margin-left: 6px;
}

.score-detail { flex: 1; min-width: 260px; }

.score-sub-grid { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
.score-sub-row  { display: flex; align-items: center; gap: 10px; }
.score-sub-label {
  font-size: 12px; font-weight: 600; color: #374151; min-width: 150px;
}
.score-dots-sm { display: flex; gap: 4px; }
.score-dot-sm {
  display: inline-block; width: 9px; height: 9px; border-radius: 50%;
}
.score-dot-sm.dot-on  { background: #003366; }
.score-dot-sm.dot-off { background: #d1d5db; }
.score-sub-num {
  font-size: 12px; font-weight: 700; color: #6b7280; min-width: 28px;
}

.score-rationale {
  font-size: 13px; color: #374151; line-height: 1.6;
  background: #f9fafb; border-left: 3px solid #bfdbfe;
  padding: 10px 14px; border-radius: 0 6px 6px 0; margin: 0 0 14px;
}
.score-flags { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.score-flag {
  font-size: 12px; font-weight: 600; background: #eff6ff; color: #1d4ed8;
  border: 1px solid #bfdbfe; border-radius: 12px; padding: 3px 12px;
}
.score-meta {
  font-size: 11px; color: #9ca3af;
}

/* ── Recalculate button ───────────────────────────────────────────────── */
.btn-recalc {
  margin-left: auto;
  padding: 6px 18px;
  background: #f59e0b;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
  white-space: nowrap;
}
.btn-recalc:hover:not(:disabled) { background: #d97706; }
.btn-recalc:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
